import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api/client';

export type ChunkQuadrant =
  | 'market_trends'
  | 'news_regulatory'
  | 'transactions'
  | 'capital_markets';

export type ChunkStatus = 'draft' | 'approved' | 'excluded';

export interface ChunkSource {
  label: string;
  url: string;
}

export interface IntelChunk {
  id: number;
  domain: string;
  sector_id: number;
  sector_name: string | null;
  week_of: string;
  event_date: string | null;
  quadrant: ChunkQuadrant;
  status: ChunkStatus;
  bold_lead: string;
  body: string;
  relevance_to_ember: string | null;
  entities: string[];
  tags: string[];
  portfolio_companies: string[];
  sources: ChunkSource[];
  source_url: string | null;
  source_extracted_text: string | null;
  relevance_confidence: 'high' | 'medium' | 'low' | null;
  extraction_notes: string | null;
  created_by: string;
  created_at: string;
  approved_by: string | null;
  approved_at: string | null;
  updated_at: string;
}

export interface ChunkCreateBody {
  sector_id: number;
  week_of: string;
  event_date?: string | null;
  quadrant: ChunkQuadrant;
  bold_lead: string;
  body: string;
  relevance_to_ember?: string | null;
  entities?: string[];
  tags?: string[];
  portfolio_companies?: string[];
  sources?: ChunkSource[];
}

export interface ChunkUpdateBody {
  sector_id?: number;
  week_of?: string;
  event_date?: string | null;
  quadrant?: ChunkQuadrant;
  bold_lead?: string;
  body?: string;
  relevance_to_ember?: string | null;
  entities?: string[];
  tags?: string[];
  portfolio_companies?: string[];
  sources?: ChunkSource[];
  status?: ChunkStatus;
}

// ── Reads ────────────────────────────────────────────────────────────────

export interface IntelChunkSearchParams {
  sectorIds?: number[];
  /** Multi-select of meeting-week Mondays (ISO YYYY-MM-DD). Filters on week_of. */
  meetingDates?: string[];
  /** Activity-date range — matches event_date (when the event happened/was
   *  published) falling back to created_at for chunks without an event_date. */
  activityFrom?: string | null;
  activityTo?: string | null;
  q?: string | null;
  status?: ChunkStatus | null;
  limit?: number;             // default 50, max 200
  offset?: number;            // default 0
}

export interface IntelChunkPage {
  items: IntelChunk[];
  total: number;
  limit: number;
  offset: number;
}

export function useIntelChunks(params: IntelChunkSearchParams) {
  const {
    sectorIds = [],
    meetingDates = [],
    activityFrom,
    activityTo,
    q,
    status,
    limit = 50,
    offset = 0,
  } = params;
  const query = new URLSearchParams();
  for (const id of sectorIds) query.append('sector_ids', String(id));
  for (const d of meetingDates) query.append('meeting_dates', d);
  if (activityFrom) query.set('activity_from', activityFrom);
  if (activityTo) query.set('activity_to', activityTo);
  if (q && q.trim()) query.set('q', q.trim());
  if (status) query.set('status', status);
  query.set('limit', String(limit));
  query.set('offset', String(offset));
  const qs = query.toString();

  return useQuery<IntelChunkPage>({
    queryKey: [
      'intel-chunks',
      [...sectorIds].sort().join(','),
      [...meetingDates].sort().join(','),
      activityFrom ?? null,
      activityTo ?? null,
      (q ?? '').trim(),
      status ?? null,
      limit,
      offset,
    ],
    queryFn: () => apiFetch<IntelChunkPage>(`/api/intel/chunks?${qs}`),
    staleTime: 30 * 1000,
    placeholderData: (prev) => prev,
  });
}

/** Distinct meeting-week Mondays that currently have at least one chunk
 *  attributed to them. Fuels the meeting-date multi-select. */
export function useIntelMeetingDates() {
  return useQuery<string[]>({
    queryKey: ['intel-chunks-meeting-dates'],
    queryFn: () => apiFetch<string[]>('/api/intel/chunks/meeting-dates'),
    staleTime: 60 * 1000,
  });
}

// ── Writes ───────────────────────────────────────────────────────────────

const invalidateChunks = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ['intel-chunks'] });
  qc.invalidateQueries({ queryKey: ['intel-chunks-meeting-dates'] });
  qc.invalidateQueries({ queryKey: ['presentation'] });
};

export function useCreateChunk() {
  const qc = useQueryClient();
  return useMutation<IntelChunk, Error, ChunkCreateBody>({
    mutationFn: (body) =>
      apiFetch<IntelChunk>('/api/intel/chunks', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateChunks(qc),
  });
}

export function useUpdateChunk() {
  const qc = useQueryClient();
  return useMutation<IntelChunk, Error, { id: number } & ChunkUpdateBody>({
    mutationFn: ({ id, ...body }) =>
      apiFetch<IntelChunk>(`/api/intel/chunks/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateChunks(qc),
  });
}

export function useDeleteChunk() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, number>({
    mutationFn: (id) => apiFetch(`/api/intel/chunks/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateChunks(qc),
  });
}

export interface DraftFromUrlBody {
  url: string;
  sector_id: number;
  week_of: string;
}

export interface DraftFromTextBody {
  text: string;
  sector_id: number;
  week_of: string;
  source_url?: string | null;
  source_label?: string | null;
}

export interface DraftExtractionError {
  error: string;
  fallback?: string;
  message?: string;
}

export function useDraftFromUrl() {
  const qc = useQueryClient();
  return useMutation<IntelChunk, Error, DraftFromUrlBody>({
    mutationFn: (body) =>
      apiFetch<IntelChunk>('/api/intel/chunks/draft-from-url', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateChunks(qc),
  });
}

export function useDraftFromText() {
  const qc = useQueryClient();
  return useMutation<IntelChunk, Error, DraftFromTextBody>({
    mutationFn: (body) =>
      apiFetch<IntelChunk>('/api/intel/chunks/draft-from-text', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateChunks(qc),
  });
}
